// TODO(post-experiment): 실험 #310(match/request 첫문항 니즈 캡처) 결과 확정 후 BaseStack 마이그레이션.
// CLAUDE.md "신규 코드는 BaseStack 사용 필수" 룰을 실험 속도/주변 코드(Chakra) 일관 위해 일시 위반한 영역.
import { Box, HStack, VStack } from "@chakra-ui/react";
import { colors, customEvent, Field, Spacing } from "goi_common";
import { useRouter } from "next/router";
import { ReactElement, useContext, useState } from "react";
import { ABEnabledContainer } from "@/components/ABEnabledContainer";
import { UrgentCallBannerContext } from "@/components/UrgentCallBannerExperiment";
import {
  ExperimentVariation,
  HackleExperimentContainer,
} from "@/components/HackleExperimentContainer";
import { markCostNeedsLoginB } from "@/utils/costNeedsResultScreen";
import {
  isQuestionContextTreatment,
  markQuestionContextLoginVariation,
  resolveQuestionContextExperimentKey,
  QuestionContextVariation,
} from "@/utils/questionContextExperiment";
import { hasAdEntryParams } from "@/utils/parlorRequestAdEntry";
import MatchRequestOnboarding from "./components/MatchRequest/MatchRequestOnboarding";
import RequestQuestionProcedureStepByStep from "./components/MatchRequest/RequestQuestionUniqueStep/RequestQuestionProcedureStepByStep";
import {
  PrevNext,
  RequestQuestionStepByStep,
} from "./components/MatchRequest/RequestQuestionUniqueStep/RequestQuestionStepByStep";
import { 장례비용절차_LIST, 장례비용절차_LIST_시설, 시설_옵션_TEXT } from "./constants/request";
import { MatchRequestNeeds } from "@/utils/matchRequestNeedsContext";

// 니즈 4종은 utils/matchRequestNeedsContext 가 소유한다(로그인 화면 마킹과 같은 집합이어야 해서).
export type AnswerType = MatchRequestNeeds | null;

// Hackle 실험 #310 - 첫문항 니즈 캡처 (/match/request 전체 모수).
// A: 기존 3옵션. B: '시설 정보 찾기' 옵션 추가.
// Z(미해결)는 ABEnabledContainer가 렌더하지 않음 → A/B 확정 후 노출(깜빡임 방지).
const EXPERIMENT_KEY = 310;

// HackleExperimentContainer effect의 의존성이라 모듈 스코프에서 참조를 고정한다.
const markQuestionContextTreatmentOnAssigned = (variation: ExperimentVariation) => {
  if (!isQuestionContextTreatment(variation)) return;
  markQuestionContextLoginVariation(variation);
  // B는 견적 결과 화면의 비용 구성 카드를 유지한다. A는 기존 #340 채택분만 따른다.
  markCostNeedsLoginB();
};

/**
 * B 안 온보딩. 첫문항과 지역 문항 **사이**에 한 장 들어간다.
 *
 * 기존 `MatchRequestOnboarding` 을 카피만 갈아 재사용한다(피그마 20497:13059).
 * 그 컴포넌트는 원래 #312, #316, `?template=cost` 에서 첫문항을 **대체**하는 화면이었다.
 * 여기서는 대체가 아니라 첫문항 뒤에 붙으므로 모수 정의("첫문항에서 비용/세 가지 다를
 * 고른 사람")가 그대로 유지된다.
 *
 * **스텝이 하나 늘어난다는 점을 알고 넣는다.** #342, #343 이 퍼널 앞에 온보딩 4장을
 * 세웠다가 양쪽 다 음수로 끝났다. 이건 1장짜리 인트로라 무게가 다르고, 같은 화면이
 * `?template=cost` 경로에서 구 #321 B 로 이겨 상시 채택된 이력이 있다. 다만 그때는
 * 첫문항을 대체했지 뒤에 붙은 게 아니라, **첫문항 다음 온보딩은 검증된 적이 없다.**
 */
function QuestionContextOnboardingFlow({ funnel }: { funnel: ReactElement }) {
  const [onboardingDone, setOnboardingDone] = useState(false);

  if (onboardingDone) return funnel;

  return (
    <MatchRequestOnboarding
      onNext={() => setOnboardingDone(true)}
      title={
        <>
          내 상황에 맞는
          <br />
          장례 예상 비용을 알아보세요
        </>
      }
      description={
        <>
          장례 희망 지역 등 몇 가지 조건만 선택하면
          <br />
          예상 비용 범위와 비용을 줄일 수 있는
          <br />
          항목을 안내해드려요.
        </>
      }
      footnote="30초면 장례 전체 견적을 받아볼 수 있어요."
      ctaLabel="내 예상 장례 비용 알아보기"
    />
  );
}

// 첫문항 + 뒷퍼널 분기. optionList만 A/B로 주입받고 나머지 플로우는 공유.
function MatchRequestFirstQuestionFlow({ optionList }: { optionList: string[] }) {
  const { isShown: isUrgentBannerShown } = useContext(UrgentCallBannerContext);
  const [selectedType, setSelectedType] = useState<AnswerType>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string>("");
  const router = useRouter();

  const confirmAnswer = (answer: string) => {
    customEvent({
      newGtm: {
        action: "match_request_answer_first_question",
      },
    });

    // 텍스트 기반 매핑(옵션 순서 변경에 안전). 시설은 전용 텍스트로, 나머지는 위치 고정(0=비용, 1=절차).
    if (answer === 시설_옵션_TEXT) {
      customEvent({ newGtm: { action: "match_request_facility_start" } });
      setSelectedType("facility");
    } else if (answer === optionList[0]) {
      customEvent({ newGtm: { action: "match_request_price_start" } });
      setSelectedType("price");
    } else if (answer === optionList[1]) {
      customEvent({ newGtm: { action: "match_request_procedure_start" } });
      setSelectedType("procedure");
    } else {
      // "둘 다 알고 싶어요"(A) / "세 가지 다 궁금해요"(B)
      customEvent({ newGtm: { action: "match_request_price_procedure_start" } });
      setSelectedType("priceAndProcedure");
    }
  };

  if (!selectedType) {
    return (
      <VStack px="16px">
        {/* now 콜 확대 B안에서는 바로 위에 임종 배너가 있어 상단 여백을 줄인다 */}
        <Spacing gap={isUrgentBannerShown ? 32 : 80} />

        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          지금 가장 걱정되는 부분은 <br />
          무엇인가요?
        </Box>

        <Spacing gap={24} />

        <Box paddingTop="24px" width="100%">
          <VStack width="100%">
            <Field.ButtonGroups
              items={optionList}
              value={selectedAnswer}
              onChange={(e) => {
                setSelectedAnswer(e);
                customEvent({
                  newGtm: {
                    path: "match_request",
                    trigger_and_target: "price_procedure-answer",
                  },
                });
                // 시설 옵션 선택 시 별도 발화 (보너스 지표: 시설 탐색 니즈 유입 비중 실측)
                if (e === 시설_옵션_TEXT) {
                  customEvent({
                    newGtm: {
                      path: "match_request",
                      trigger_and_target: "facility-answer",
                    },
                  });
                }
              }}
              buttonWidth="100%"
              borderColor={colors.white}
            />
          </VStack>
        </Box>

        <Spacing gap={24} />

        <HStack
          paddingTop="4px"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          gap="24px"
        >
          {/* 초기 질문은 진짜 첫 단계라 이전 없음. disabled 상태로 노출(기존 UI 유지). */}
          <Box width="100%">
            <PrevNext disabled text="이전" variant="filled" onClick={() => {}} />
          </Box>
          <Box width="100%">
            <PrevNext
              disabled={!selectedAnswer}
              text="다음"
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: "match_request",
                    trigger_and_target: "move-next-initial",
                  },
                });
                confirmAnswer(selectedAnswer);
              }}
            />
          </Box>
        </HStack>

        <Spacing gap={360} />
      </VStack>
    );
  }

  if (selectedType === "procedure") {
    return <RequestQuestionProcedureStepByStep onBack={() => setSelectedType(null)} />;
  }

  if (selectedType === "price" || selectedType === "priceAndProcedure") {
    const isAdEntry = hasAdEntryParams(router.query);
    const needs = selectedType;
    const funnel = (variation: QuestionContextVariation) => (
      <RequestQuestionStepByStep
        showExpectedPriceRange={true}
        type={needs}
        needs={needs}
        questionContextVariation={variation}
      />
    );
    // 니즈 x 유입 2x2. 셀을 가르는 근거는 resolveQuestionContextExperimentKey 주석 참고.
    // contextExtra 는 셀 판정과 무관하게 남긴다. Hackle 자동 노출과 모수가 어긋나 분모로는
    // 못 쓰지만, 결과가 이상할 때 배정이 의도대로 갈렸는지 되짚는 데는 쓸 수 있다.
    //
    // B 에만 온보딩 한 장이 먼저 붙는다(QuestionContextOnboardingFlow). A 는 첫문항에서
    // 바로 지역 문항으로 가는 현행 그대로다.
    return (
      <HackleExperimentContainer
        experimentKey={resolveQuestionContextExperimentKey({ needs, isAdEntry })}
        onAssigned={markQuestionContextTreatmentOnAssigned}
        variants={{
          A: funnel("A"),
          B: <QuestionContextOnboardingFlow funnel={funnel("B")} />,
        }}
        contextExtra={{ needs, ad_entry: isAdEntry ? "ad" : "organic" }}
      />
    );
  }

  if (selectedType === "facility") {
    return (
      <RequestQuestionStepByStep showExpectedPriceRange={true} type="facility" needs="facility" />
    );
  }

  return null;
}

export default function MatchRequestInitialQuestionContainer() {
  // Z(미해결)면 ABEnabledContainer가 null 반환 → A/B 확정 후에만 첫문항 노출.
  return (
    <ABEnabledContainer
      experimentKey={EXPERIMENT_KEY}
      ComponentA={<MatchRequestFirstQuestionFlow optionList={장례비용절차_LIST} />}
      ComponentB={<MatchRequestFirstQuestionFlow optionList={장례비용절차_LIST_시설} />}
    />
  );
}
